import { Button, Group, Paper, Stack, Text } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { FREIGHT_CLASS_OPTIONS } from './freight-class-options';
import { freightFormMessages } from './freight-form-messages';
import { WeightInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';

import type { FreightClass, LtlCommodity } from '../../../../types/ltl';
import type { Weight } from '../../../../types/weight';

type CommodityCardProps = {
  commodity: LtlCommodity;
  index: number;
  onChange: (updated: LtlCommodity) => void;
  onRemove?: () => void;
};

export const CommodityCard = ({ commodity, index, onChange, onRemove }: CommodityCardProps) => {
  const intl = useIntl();

  const weightValue: Weight = {
    value: commodity.weight,
    unit: commodity.weightUnit ?? 'Ounces',
  };

  const handleWeightChange = (next: Weight) => {
    onChange({ ...commodity, weight: next.value });
  };

  const handleClassChange = (val: string | null) => {
    onChange({ ...commodity, freightClass: (val as FreightClass) ?? '' });
  };

  return (
    <Paper bg='gray.0' data-testid={`commodity-card-${index}`} p='sm' withBorder>
      <Stack gap='xs'>
        <Group justify='space-between'>
          <Text fw={500} size='sm'>
            {intl.formatMessage(freightFormMessages.commodityTitle, { number: index + 1 })}
          </Text>
          {onRemove ? (
            <Button color='red' data-testid={`remove-commodity-${index}`} onClick={onRemove} size='xs' variant='ghost'>
              <FormattedMessage {...freightFormMessages.removeCommodity} />
            </Button>
          ) : null}
        </Group>
        <Group grow>
          <WeightInput onBlur={handleWeightChange} showMinorUnit={false} value={weightValue} />
          <Dropdown
            data={FREIGHT_CLASS_OPTIONS}
            data-testid={`commodity-class-${index}`}
            label={intl.formatMessage(freightFormMessages.freightClassLabel)}
            onChange={handleClassChange}
            placeholder={intl.formatMessage(freightFormMessages.freightClassPlaceholder)}
            value={commodity.freightClass || null}
          />
        </Group>
      </Stack>
    </Paper>
  );
};
